import '@/configs/firebase-functions';
import '@/configs/firebase-firestore';
import type { QueryFunctionContext } from '@tanstack/vue-query';
import { addDays } from 'date-fns';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit as limitConstraint,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { cloneDeep } from 'lodash-es';
import { type MaybeRef, unref } from 'vue';
import { z } from 'zod';

import {
  BookingCreateZ,
  bookingDocMapper,
  type BookingId,
  BookingIdZ,
  BookingLocalZ,
  type BookingStatus,
  BookingStatusZ,
  BookingUpdateZ,
  BookingZ,
  calendarDocMapper,
  CalendarZ,
  checkRoomEquipUnavailable,
  checkRoomHoursUnavailable,
  EquipBookingAdminLocalZ,
  EquipBookingLocalZ,
  equipCalendarDocMapper,
  EquipCalendarZ,
  type EquipGroupId,
  EquipGroupZ,
  type EquipPromo,
  type EquipSpan,
  EquipZ,
  isBookingUnreservedOrExpire,
  isNotExpired,
  paymentDocMapper,
  type PaymentId,
  PaymentIdZ,
  PaymentOnlineViaZ,
  PaymentZ,
  type PromoId,
  PromoZ,
  RoomBookingAdminLocalZ,
  RoomBookingLocalZ,
  type RoomId,
  RoomPricingZ,
  type RoomPromo,
  RoomZ,
  UserCalendarZ,
} from '@scaffold/common/booking';
import {
  getEquipGroups as getEquipGroupsCommon,
  getRooms as getRoomsCommon,
} from '@scaffold/common/data';
import settings from '@scaffold/common/settings';
import type { UserId } from '@scaffold/common/user';
import { arrayAndMap, memoizeAsync } from '@scaffold/common/utils';

import { toast } from '@/context';
import { callable } from '@/core/callable';
import { NotFoundError } from '@/core/errors';
import {
  buildConverter,
  extractDocs,
  extractExistingDoc,
} from '@/core/firestore';
import { createLocalStore } from '@/core/local-store';
import { getNaiveToday } from '@/utils/booking';

export const getRooms = memoizeAsync((locale: string) =>
  getRoomsCommon(locale).then((rooms) =>
    arrayAndMap(z.array(RoomZ).parse(rooms)),
  ),
);

export const getEquipGroups = memoizeAsync((locale: string) =>
  getEquipGroupsCommon(locale).then((groups) =>
    arrayAndMap(z.array(EquipGroupZ).parse(groups)),
  ),
);

export const getRoomPricing = (locale: string, promoId: PromoId) =>
  getDocs(
    query(
      collection(getFirestore(), 'lang', locale.split('-')[0], 'pricing'),
      where('promo', '==', promoId),
      where('end', '>=', getNaiveToday()),
      orderBy('end', 'asc'),
    ).withConverter(buildConverter(RoomPricingZ)),
  ).then(extractDocs);
getRoomPricing.query = (
  locale: MaybeRef<string>,
  promoId: MaybeRef<PromoId | undefined>,
) => ({
  queryKey: ['pricing', promoId, locale] as const,
  queryFn: () => getRoomPricing(unref(locale), unref(promoId)!),
  enabled: () => !!unref(promoId),
});

export const getDayCalendar = (day: Date) =>
  // getMockDayCalendar(day) ??
  getDocs(
    query(
      collectionGroup(getFirestore(), 'calendar'),
      where('start', '>=', day),
      where('start', '<', addDays(day, 1)),
      orderBy('start', 'asc'),
    ).withConverter(buildConverter(CalendarZ, calendarDocMapper)),
  )
    .then(extractDocs)
    .then((calendar) => calendar.filter(isNotExpired));
getDayCalendar.invalidateFilters = {
  queryKey: ['calendar', 'day'] as const,
};
getDayCalendar.query = (
  day: MaybeRef<Date>,
  enabled: MaybeRef<boolean> = true,
) => ({
  queryKey: ['calendar', 'day', day] as const,
  queryFn: () => getDayCalendar(unref(day)),
  enabled,
});

export const getDayEquipCalendar = (day: Date) =>
  getDocs(
    query(
      collectionGroup(getFirestore(), 'equip_calendar'),
      where('span', '==', 'hourly'),
      where('start', '>=', day),
      where('start', '<', addDays(day, 1)),
      orderBy('start', 'asc'),
    ).withConverter(buildConverter(EquipCalendarZ, equipCalendarDocMapper)),
  )
    .then(extractDocs)
    .then((calendar) => calendar.filter(isNotExpired));
getDayEquipCalendar.invalidateFilters = {
  queryKey: ['equipCalendar', 'day'] as const,
};
getDayEquipCalendar.query = (day: MaybeRef<Date>) => ({
  queryKey: ['equipCalendar', 'day', day] as const,
  queryFn: () => getDayEquipCalendar(unref(day)),
});

export const getGroupEquipCalendar = (
  groupId: EquipGroupId,
  start: Date,
  end: Date,
) =>
  getDocs(
    query(
      collectionGroup(getFirestore(), 'equip_calendar'),
      where('group', '==', groupId),
      where('span', '==', 'daily'),
      where('start', '<', end),
      where('end', '>', start),
      orderBy('start', 'asc'),
    ).withConverter(buildConverter(EquipCalendarZ, equipCalendarDocMapper)),
  )
    .then(extractDocs)
    .then((calendar) => calendar.filter(isNotExpired));
getGroupEquipCalendar.invalidateFilters = {
  queryKey: ['equipCalendar', 'group'] as const,
};
getGroupEquipCalendar.query = (
  groupId: MaybeRef<EquipGroupId | undefined>,
  start: MaybeRef<Date | undefined>,
  end: MaybeRef<Date | undefined>,
) => ({
  queryKey: ['equipCalendar', 'group', groupId, start, end] as const,
  queryFn: () =>
    getGroupEquipCalendar(unref(groupId)!, unref(start)!, unref(end)!),
  enabled: () => !!unref(groupId) && !!unref(start) && !!unref(start),
});

export const getEquipCalendarByNames = (
  names: string[],
  start: Date,
  end: Date,
) =>
  getDocs(
    query(
      collectionGroup(getFirestore(), 'equip_calendar'),
      where('name', 'in', names),
      where('span', '==', 'daily'),
      where('start', '<', end),
      where('end', '>', start),
      orderBy('start', 'asc'),
    ).withConverter(buildConverter(EquipCalendarZ, equipCalendarDocMapper)),
  )
    .then(extractDocs)
    .then((calendar) => calendar.filter(isNotExpired));

// Removed instances have no quantity. The quantity range next to the
// span and room filters needs composite indexes.
export const getEquip = (span: EquipSpan, room?: RoomId) =>
  getDocs(
    query(
      collection(getFirestore(), 'equip'),
      where('span', '==', span),
      ...(room ? [where('room', '==', room)] : []),
      where('quantity', '>', 0),
    ).withConverter(buildConverter(EquipZ)),
  ).then(extractDocs);
getEquip.query = (span: EquipSpan, room?: RoomId) => ({
  queryKey: ['equip', 'span', span, room] as const,
  queryFn: () => getEquip(span, room),
});

export const createBooking = callable(
  'booking-create',
  BookingCreateZ,
  z.object({ bid: BookingIdZ, status: BookingStatusZ }),
);

export const removeBooking = callable(
  'booking-remove',
  z.tuple([BookingIdZ]),
  BookingLocalZ,
);

export const cancelBooking = callable(
  'booking-cancel',
  z.tuple([BookingIdZ, z.string()]),
  z.void(),
);

export const updateBooking = callable(
  'booking-update',
  BookingUpdateZ,
  z.void(),
);

export const paymentCreate = callable(
  'booking-createPayment',
  z.tuple([BookingIdZ, PaymentOnlineViaZ, z.string()]),
  z.object({ pid: PaymentIdZ }),
);

export const paymentCheck = callable(
  'booking-checkPayment',
  z.tuple([BookingIdZ, z.boolean().optional()]),
  z.object({
    status: BookingStatusZ,
    paid: z.number(),
    pid: PaymentIdZ.optional(),
  }),
);

export const getUserBookings = (
  uid: UserId,
  statuses: BookingStatus | BookingStatus[],
  limit = 10,
) =>
  getDocs(
    query(
      collection(getFirestore(), 'booking'),
      where('uid', '==', uid),
      ...(Array.isArray(statuses)
        ? [where('status', 'in', statuses)]
        : [where('status', '==', statuses)]),
      limitConstraint(limit),
    ).withConverter(buildConverter(BookingZ, bookingDocMapper)),
  ).then(extractDocs);

export const getBooking = (id: BookingId) =>
  getDoc(
    doc(getFirestore(), 'booking', id).withConverter(
      buildConverter(BookingZ, bookingDocMapper),
    ),
  ).then(extractExistingDoc);
getBooking.invalidateFilters = (id: BookingId) => ({
  queryKey: ['booking', id] as const,
});
getBooking.query = (id: MaybeRef<BookingId | undefined>) => ({
  queryKey: ['booking', id] as const,
  queryFn: async ({ client }: QueryFunctionContext) => {
    const booking = await getBooking(unref(id)!);

    if (isBookingUnreservedOrExpire(booking.status)) {
      if (booking.type === 'room' && booking.equipment) {
        const calendar = await client.fetchQuery(
          getBookingCalendar.query(id, booking.uid, true),
        );

        await checkRoomEquipUnavailable(
          calendar,
          booking.equipment,
          (day: Date) => client.fetchQuery(getDayEquipCalendar.query(day)),
        );
      }
    }

    return booking;
  },
  enabled: () => !!unref(id),
});

export const getBookingCalendar = (bid: BookingId, uid: UserId) =>
  getDocs(
    query(
      collection(getFirestore(), 'booking', bid, 'user_calendar'),
      where('uid', '==', uid),
      orderBy('start', 'asc'),
    ).withConverter(buildConverter(UserCalendarZ)),
  ).then(extractDocs);
getBookingCalendar.invalidateFilters = (
  bid: BookingId,
  availability?: true,
) => ({
  queryKey: availability
    ? (['userCalendar', bid, availability] as const)
    : (['userCalendar'] as const),
});
getBookingCalendar.query = (
  bid: MaybeRef<BookingId | undefined>,
  uid: MaybeRef<UserId | undefined>,
  availability: MaybeRef<boolean>,
) => ({
  queryKey: ['userCalendar', bid, availability] as const,
  queryFn: async ({ client }: QueryFunctionContext) => {
    const bookingId = unref(bid)!;

    if (!unref(availability))
      return await getBookingCalendar(bookingId, unref(uid)!);

    const calendar = cloneDeep(
      await client.fetchQuery({
        queryKey: ['userCalendar', bid, false] as const,
        queryFn: () => getBookingCalendar(bookingId, unref(uid)!),
      }),
    );

    await checkRoomHoursUnavailable(undefined, calendar, (day: Date) =>
      client
        .fetchQuery(getDayCalendar.query(day))
        .then((calendar) => calendar.filter((c) => c.bid !== bookingId)),
    );

    return calendar;
  },
  enabled: () => !!unref(uid) && !!unref(bid),
});

export const getBookingPayments = (bid: BookingId, uid: UserId) =>
  getDocs(
    query(
      collection(getFirestore(), 'payments'),
      where('bid', '==', bid),
      where('uid', '==', uid),
      orderBy('created', 'desc'),
    ).withConverter(buildConverter(PaymentZ, paymentDocMapper)),
  ).then(extractDocs);
getBookingPayments.invalidateFilters = (bid: BookingId) => ({
  queryKey: ['payments', bid] as const,
});
getBookingPayments.query = (
  bid: MaybeRef<BookingId | undefined>,
  uid: MaybeRef<UserId | undefined>,
) => ({
  queryKey: ['payments', bid] as const,
  queryFn: () => getBookingPayments(unref(bid)!, unref(uid)!),
  enabled: () => !!unref(uid) && !!unref(bid),
});

export const roomBookingLocalStore = createLocalStore(
  'roomBookingLocalStore',
  RoomBookingLocalZ.partial(),
);

export const roomBookingAdminLocalStore = createLocalStore(
  'roomBookingAdminLocalStore',
  RoomBookingAdminLocalZ.partial(),
);

export const equipBookingLocalStore = createLocalStore(
  'equipBookingLocalStore',
  EquipBookingLocalZ.partial(),
);

export const equipBookingAdminLocalStore = createLocalStore(
  'equipBookingAdminLocalStore',
  EquipBookingAdminLocalZ.partial(),
);

export const getPromo = (id: PromoId) =>
  getDoc(
    doc(getFirestore(), 'promo', id).withConverter(buildConverter(PromoZ)),
  ).then(extractExistingDoc);
getPromo.queryRoom = (
  locale: MaybeRef<string>,
  id: MaybeRef<PromoId | undefined>,
) => ({
  queryKey: ['promo', id, 'pricing', locale] as const,
  queryFn: async ({ client }: QueryFunctionContext): Promise<RoomPromo> => {
    const promoId = unref(id)!;

    const [promo, pricing, defaultPricing] = await Promise.all(
      promoId === 'NONE'
        ? [
            settings.payment.promo.room,
            [],
            client.fetchQuery(getRoomPricing.query(locale, 'NONE')),
          ]
        : [
            getPromo(promoId),
            client.fetchQuery(getRoomPricing.query(locale, promoId)),
            client.fetchQuery(getRoomPricing.query(locale, 'NONE')),
          ],
    );
    if (promo.type !== 'room')
      throw new NotFoundError(`Expected 'room' type, got '${promo.type}'`);

    return { ...promo, pricing: [...pricing, ...defaultPricing] };
  },
  enabled: () => !!unref(id),
});
getPromo.queryEquip = (id: MaybeRef<PromoId | undefined>) => ({
  queryKey: ['promo', id, 'equip'] as const,
  queryFn: async (): Promise<EquipPromo> => {
    const promoId = unref(id)!;

    const promo =
      promoId === 'NONE'
        ? settings.payment.promo.equip
        : await getPromo(promoId);
    if (promo.type !== 'equip')
      throw new NotFoundError(`Expected 'equip' type, got '${promo.type}'`);

    return promo;
  },
  enabled: () => !!unref(id),
});

export const getInvoicePdfResource = callable(
  'invoice-getPdfResource',
  z.tuple([PaymentIdZ]),
  z.url(),
);

export async function openInvoice(pid: PaymentId) {
  const invoiceWindow = open('', '_blank');
  if (!invoiceWindow) {
    toast.error($t`Pop-up blocked`);

    return;
  }

  invoiceWindow.document.write($t`Preparing invoice document…`);

  const loading = toast.default($t`Preparing invoice document…`, {
    duration: 0,
    dismissible: false,
  });
  try {
    const src = await getInvoicePdfResource(pid);
    invoiceWindow.location.href = src;
  } catch (err) {
    toast.error($t`Failed to open invoice document`);

    invoiceWindow.close();

    throw err;
  } finally {
    loading.dismiss();
  }
}

export const getExistingBookingId = (uid: UserId) =>
  getUserBookings(uid, ['NEW', 'EXPIRE'], 1).then((list) => list[0]?.id);

export const getNextRehearsal = (uid: UserId, from: Date) =>
  getDocs(
    query(
      collectionGroup(getFirestore(), 'user_calendar'),
      where('uid', '==', uid),
      where('status', 'in', ['PAID', 'UNPAID']),
      where('start', '>', from),
      orderBy('start', 'asc'),
      limitConstraint(1),
    ).withConverter(buildConverter(UserCalendarZ)),
  )
    .then(extractDocs)
    .then((calendar) => (calendar.length ? calendar[0] : null));
getNextRehearsal.query = (
  uid: MaybeRef<UserId | null | undefined>,
  from: MaybeRef<Date>,
) => ({
  queryKey: ['userCalendar', 'nextRehearsalDate', uid],
  queryFn: () => getNextRehearsal(unref(uid)!, unref(from)!),
  enabled: () => !!unref(uid) && !!unref(from),
});
